import { create } from 'zustand'

import {
  createAiPurchaseApi,
  fetchAiMembershipProfileApi,
} from '@/network/api/ai/membership'
import type { AiMembershipProfileDto } from '@/network/api/ai/membership'
import type { PaymentChannel } from '@/network/api/payment/type'
import type { MembershipTier } from '@/views/AI/constants/membershipPlans'
import { TIER_LABEL } from '@/views/AI/constants/membershipPlans'

export type AiUsageRecordType = 'image_text' | 'multi_view' | 'batch'

export interface AiUsageRecord {
  id: string
  time: string
  type: AiUsageRecordType | string
  typeLabel: string
  quantity: number
  consumeMethod: '积分' | '次数'
  consumeAmount: string
}

export interface AiPointRecord {
  id: number
  time: string
  recordType: 'credit' | 'debit'
  recordTypeLabel: string
  sourceLabel: string
  amount: number
  amountLabel: string
  balanceAfter: number
  description?: string
}

const POINT_SOURCE_LABEL: Record<number, string> = {
  1: 'AI 生成消耗',
  2: '加油包充值',
  3: '会员赠送',
  4: '生成失败退还',
  5: '管理员调整',
}

export interface AiPurchaseResult {
  orderId: number
  payOrderId: number
  outTradeNo: string
  qrCode: string
  displayAmount: string
  channel: PaymentChannel
}

interface AiMembershipState {
  loaded: boolean
  loading: boolean
  tier: MembershipTier
  tierLabel: string
  expireDate: string | null
  availablePoints: number
  availablePriorityCount: number
  usageRecords: AiUsageRecord[]
  pointRecords: AiPointRecord[]
  loadProfile: () => Promise<void>
  purchase: (
    productType: 'membership' | 'refill' | 'single' | 'storage_expansion',
    productRef: string,
    channel?: PaymentChannel,
  ) => Promise<AiPurchaseResult>
}

const mapUsageRecords = (
  records: NonNullable<AiMembershipProfileDto['usageRecords']>,
): AiUsageRecord[] =>
  records.map((item) => ({
    id: item.id,
    time: new Date(item.time).toLocaleString('zh-CN'),
    type: item.type,
    typeLabel: item.typeLabel,
    quantity: item.quantity,
    consumeMethod: '积分',
    consumeAmount: item.consumeAmount,
  }))

const mapPointRecords = (
  records: NonNullable<AiMembershipProfileDto['pointRecords']>,
): AiPointRecord[] =>
  records.map((item) => {
    const isCredit = item.type === 1
    const sourceLabel =
      item.sourceLabel?.trim() ||
      POINT_SOURCE_LABEL[item.sourceType] ||
      '其他'
    return {
      id: item.id,
      time: new Date(item.createdAt).toLocaleString('zh-CN'),
      recordType: isCredit ? 'credit' : 'debit',
      recordTypeLabel: isCredit ? '入账' : '出账',
      sourceLabel,
      amount: item.amount,
      amountLabel: isCredit ? `+${item.amount}` : `-${item.amount}`,
      balanceAfter: item.availableAfter,
      description: item.description,
    }
  })

export const useAiMembershipStore = create<AiMembershipState>((set, get) => ({
  loaded: false,
  loading: false,
  tier: 'none',
  tierLabel: TIER_LABEL.none,
  expireDate: null,
  availablePoints: 0,
  availablePriorityCount: 0,
  usageRecords: [],
  pointRecords: [],
  loadProfile: async () => {
    if (get().loading) return
    set({ loading: true })
    try {
      const res = await fetchAiMembershipProfileApi()
      const data = res?.data
      if (!data) return
      const tier = (data.tier in TIER_LABEL ? data.tier : 'none') as MembershipTier
      set({
        loaded: true,
        tier,
        tierLabel: data.tierLabel || TIER_LABEL[tier] || TIER_LABEL.none,
        expireDate: data.expireDate ? new Date(data.expireDate).toLocaleDateString('zh-CN') : null,
        availablePoints: data.availablePoints ?? 0,
        availablePriorityCount: 0,
        usageRecords: mapUsageRecords(data.usageRecords ?? []),
        pointRecords: mapPointRecords(data.pointRecords ?? []),
      })
    } finally {
      set({ loading: false })
    }
  },
  purchase: async (productType, productRef, channel = 'WXPAY') => {
    const res = await createAiPurchaseApi({ product_type: productType, product_ref: productRef, channel })
    const data = res?.data as {
      orderId?: number
      order_id?: number
      payOrderId?: number
      pay_order_id?: number
      outTradeNo?: string
      out_trade_no?: string
      qrCode?: string
      qr_code?: string
      displayAmount?: string
      display_amount?: string
      channel?: PaymentChannel
    } | undefined

    const orderId = data?.orderId ?? data?.order_id
    const payOrderId = data?.payOrderId ?? data?.pay_order_id
    const outTradeNo = data?.outTradeNo ?? data?.out_trade_no ?? ''
    const qrCode = data?.qrCode ?? data?.qr_code ?? ''
    const displayAmount = data?.displayAmount ?? data?.display_amount ?? ''

    if (!orderId || !payOrderId) throw new Error('创建订单失败')
    if (!qrCode) throw new Error('未获取到支付二维码')

    return {
      orderId,
      payOrderId,
      outTradeNo,
      qrCode,
      displayAmount,
      channel: data?.channel ?? channel,
    }
  },
}))
